// İSG uzmanı portalı — "Şu an sahada" (api-contract §11, tenant-scoped). Sahada bulunan
// ziyaretçiler = `GET /visits?status=OnSite` — acil durum / tahliye için en kritik ekran.
// Üstte "Ziyaretçi Girişi" (check-in) birincil butonu; her satırda hızlı Çıkış (check-out)
// → ziyaretçi listeden düşer. Sıra backend'de check-in azalan (istemci sort göndermez).
// Carbon liste anatomisi (S33 Slice 4): h2 + New → Card(TableToolbar → Table → Pagination).

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  type VisitDto,
  currentCulture,
  resolveApiErrorMessage,
  visitsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, SearchX, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { CheckInModal } from '../../components/visitors/CheckInModal';
import { formatDateTime } from '../../lib/format';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { visitsKeys } from '../../lib/visitsKeys';

const PAGE_SIZE = 20;

/** Ziyaretçinin görünen tam adı (özet bloktan). */
function visitorName(visit: VisitDto): string {
  return `${visit.visitor.firstName} ${visit.visitor.lastName}`.trim();
}

/** Saha/şube görüntü metni — saha yoksa şubeye düşer, ikisi de yoksa tire. */
function locationText(visit: VisitDto): string {
  return visit.siteName ?? visit.branchName ?? '—';
}

export function OnSiteVisitorsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const culture = currentCulture();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutVisit, setCheckOutVisit] = useState<VisitDto | null>(null);

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  // "Şu an sahada" — status=OnSite sabit; sort GÖNDERİLMEZ (backend check-in azalan).
  const params = {
    status: 'OnSite' as const,
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
  };

  const query = useQuery({
    queryKey: visitsKeys.onSite(params),
    queryFn: () => visitsApi.listVisits(params),
    placeholderData: (prev) => prev,
  });

  const checkOutMutation = useMutation({
    mutationFn: (uuid: string) => visitsApi.checkOut(uuid),
    onSuccess: (_data, _uuid) => {
      queryClient.invalidateQueries({ queryKey: visitsKeys.list() });
      setMessage(t('visitors.onSite.checkedOut', { name: visitorName(checkOutVisit as VisitDto) }));
      setCheckOutVisit(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCheckInOpen(true);
          }}
        >
          <UserPlus className="h-4 w-4" aria-hidden="true" />
          {t('visitors.onSite.checkIn')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('visitors.onSite.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('visitors.common.search')}
            searchPlaceholder={t('visitors.onSite.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('visitors.onSite.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('visitors.onSite.col.visitor')}</TableHead>
                <TableHead>{t('visitors.onSite.col.location')}</TableHead>
                <TableHead>{t('visitors.onSite.col.host')}</TableHead>
                <TableHead>{t('visitors.onSite.col.purpose')}</TableHead>
                <TableHead>{t('visitors.onSite.col.checkInAt')}</TableHead>
                <TableHead className="text-right">{t('visitors.common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={6} />
              ) : query.isError ? (
                <TableMessageRow colSpan={6}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={6}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('visitors.onSite.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={Users}
                      title={t('visitors.onSite.empty')}
                      description={t('visitors.onSite.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCheckInOpen(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4" aria-hidden="true" />
                          {t('visitors.onSite.checkIn')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((visit) => (
                  <TableRow key={visit.uuid}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-fg">{visitorName(visit)}</span>
                        <span className="text-xs text-muted">
                          {visit.visitor.phone ?? ''}
                          {visit.visitor.phone && visit.visitor.organization ? ' · ' : ''}
                          {visit.visitor.organization ?? ''}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted">{locationText(visit)}</TableCell>
                    <TableCell className="text-muted">{visit.hostName ?? '—'}</TableCell>
                    <TableCell className="text-muted">{visit.purpose ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDateTime(visit.checkInAt, culture)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCheckOutVisit(visit);
                          }}
                        >
                          <LogOut className="h-4 w-4" aria-hidden="true" />
                          {t('visitors.onSite.action.checkOut')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('visitors.pagination.nav'),
                previous: t('visitors.pagination.previous'),
                next: t('visitors.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      {/* S36 #14 — CheckInModal zaten kendi içinde bir başarı ekranı gösterir (rozet kodu
          dahil) ve kapanana kadar açık kalır; sayfa düzeyinde AYRICA bir "Alert" barı basmak
          aynı mesajı İKİ KEZ (modalin İÇİNDE + arkada) gösterirdi. Sayfa barı yalnız
          check-out için kalır (o akışın kendi bir başarı ekranı YOK). */}
      <CheckInModal open={checkInOpen} onOpenChange={setCheckInOpen} />
      <ConfirmDialog
        open={checkOutVisit !== null}
        onOpenChange={(open) => !open && setCheckOutVisit(null)}
        title={t('visitors.onSite.checkOut.title')}
        body={t('visitors.onSite.checkOut.body', {
          name: checkOutVisit ? visitorName(checkOutVisit) : '',
        })}
        confirmLabel={t('visitors.onSite.checkOut.confirm')}
        confirmVariant="primary"
        loading={checkOutMutation.isPending}
        error={checkOutMutation.isError ? checkOutMutation.error : undefined}
        onConfirm={() => checkOutVisit && checkOutMutation.mutate(checkOutVisit.uuid)}
      />
    </div>
  );
}
